import type L from "leaflet";
import { LAYER_Z_INDEX } from "../constants/layerZIndex";
import { MAP_EXPORT_PARCEL_POLYGON_CLASS } from "../constants/mapExport";
import {
  CURRENT_LOCATION_PANE_NAME,
  COVERAGE_STROKE_PANE_CLASS,
} from "../constants/pane";

/**
 * 地図出力（PNG/PDF/プレビュー）時のレイヤー可視性制御ユーティリティ。
 *
 * ゼンリン地図のライセンス上、他レイヤーと混在した状態での出力が制限される
 * ケースがあるため、出力時はゼンリン関連レイヤーと一部の追加レイヤー、
 * ピン、描画図形のみを残して他を一時的に非表示にする必要がある。
 *
 * 本モジュールはこの「出力用に表示を絞り込む」ロジックを一本化したもので、
 * 元 useMapImageExport.ts の applyExportVisibility 2 系統を統合している。
 */

/**
 * 出力に含めることを許可する追加データレイヤーの zIndex 一覧。
 *
 * NOTE: ここが新規レイヤー追加時の唯一の編集ポイント。
 *       ここを更新すれば PNG / PDF / プレビューすべての出力経路に反映される。
 */
export const EXPORTABLE_ADDITIONAL_Z_INDEXES = [
  LAYER_Z_INDEX.AREA_USE_PURPOSE, // 用途地域
  LAYER_Z_INDEX.LAND_PRICE, // 公示価格
  LAYER_Z_INDEX.TRADE_PRICE, // 取引価格
  LAYER_Z_INDEX.HAZARD, // ハザードマップ（洪水、津波、土砂災害など）
  LAYER_Z_INDEX.SCHOOL_AREA, // 学校区
  LAYER_Z_INDEX.IDOL_FARMLAND, // 遊休農地
  LAYER_Z_INDEX.CITY_PLANNING_AREA, // 都市計画区域
  LAYER_Z_INDEX.FIRE_PROTECTION_AREA, // 防火・準防火地域
  LAYER_Z_INDEX.HIGH_LEVEL_AREA, // 高度地区
  LAYER_Z_INDEX.HIGH_LEVEL_USE_AREA, // 高度利用地区
  LAYER_Z_INDEX.DISASTER_RISK_AREA, // 災害危険区域
] as const;

/**
 * 出力時にゼンリン地図として扱う zIndex 一覧。
 * これらに該当する overlayPane / tilePane の子要素はゼンリン由来とみなして
 * 出力に含める。
 */
export const ZENRIN_Z_INDEXES = [
  LAYER_Z_INDEX.ZENRIN_RESIDENTIAL, // 10: ゼンリン住宅地図（表札なし）
  LAYER_Z_INDEX.ZENRIN_BLUE_MAP, // 11: ゼンリンブルーマップ
  LAYER_Z_INDEX.ZENRIN_FETCHED_IMAGE, // 12: 取得した住宅地図（表札あり）
] as const;

export interface ApplyExportVisibilityOptions {
  /** 追加データレイヤー（用途地域・ハザード等）を含めるか */
  includeLayers: boolean;
  /** ピン（marker / shadow）を含めるか */
  includePins: boolean;
  /** 描画図形（drawnShapesPane）を含めるか */
  includeDrawnShapes: boolean;
  /** 現在地マーカー（currentLocationPane）を含めるか */
  includeCurrentLocation?: boolean;
}

export interface ApplyExportVisibilityParams {
  /** 対象の Leaflet マップインスタンス */
  mapInstance: L.Map;
  /** どのレイヤー種別を出力に含めるか */
  exportOptions: ApplyExportVisibilityOptions;
  /** ゼンリン画像オーバーレイの URL（識別用） */
  zenrinImageOverlayUrl?: string;
  /**
   * 要素を非表示にするときに呼び出されるコールバック。
   * 元の display 値の保存と復元責任は呼び出し側が持つ。
   */
  hideElement: (el: HTMLElement | SVGElement) => void;
}

/**
 * 出力対象外の要素を一時的に非表示にする。
 *
 * `exportOptions` に指定された各種フラグ（includeLayers, includePins, includeDrawnShapes, includeCurrentLocation）に
 * 基づき、対象外のペイン（markerPane, shadowPane, drawnShapesPane, currentLocationPane 等）や
 * レイヤーの DOM 要素に対して `hideElement` コールバックを呼び出す。
 *
 * 副作用: 引数の hideElement が呼ばれるごとに DOM 要素の style.display が
 *         書き換わる。呼び出し側で復元責任を持つこと（restoreDisplayStack 等）。
 *
 * 共通化前は handleDownloadMapImage と renderMapExportCanvas に重複実装が
 * あり、HIGH_LEVEL_AREA / DISASTER_RISK_AREA の追加が片側だけで行われて
 * いた。本関数では EXPORTABLE_ADDITIONAL_Z_INDEXES に揃えて両方反映する。
 */
export const applyExportVisibility = ({
  mapInstance,
  exportOptions,
  zenrinImageOverlayUrl,
  hideElement,
}: ApplyExportVisibilityParams): void => {
  const panes = mapInstance.getPanes();

  // ツールチップとポップアップは常に非表示。
  // markerPane / shadowPane / drawnShapesPane / currentLocationPane は include フラグで制御する。
  const panesToHide = [
    "tooltipPane",
    "popupPane",
    "markerPane",
    "shadowPane",
    "drawnShapesPane",
    CURRENT_LOCATION_PANE_NAME,
  ].filter((pane) => {
    if (
      exportOptions.includePins &&
      (pane === "markerPane" || pane === "shadowPane")
    ) {
      return false;
    }
    if (exportOptions.includeDrawnShapes && pane === "drawnShapesPane") {
      return false;
    }
    if (
      exportOptions.includeCurrentLocation &&
      pane === CURRENT_LOCATION_PANE_NAME
    ) {
      return false;
    }
    return true;
  });
  panesToHide.forEach((name) => {
    const pane = panes[name as keyof typeof panes];
    if (pane) {
      hideElement(pane);
    }
  });

  const zenrinZIndexStrings = ZENRIN_Z_INDEXES.map((z) => String(z));
  const additionalZIndexStrings = exportOptions.includeLayers
    ? EXPORTABLE_ADDITIONAL_Z_INDEXES.map((z) => String(z))
    : [];
  const allowedZIndexes = new Set([
    ...zenrinZIndexStrings,
    ...additionalZIndexStrings,
  ]);

  // tilePane:
  //  - ベースマップ層 (z < 100) は現在の表示状態を尊重して触らない。
  //    OSM は Leaflet 既定で zIndex=1 となり、allowedZIndexes に
  //    含まれないため明示的に除外しないと出力から消えてしまう。
  //  - データレイヤー層 (z >= 100) は includeLayers の制御に従う。
  const tilePane = panes.tilePane;
  if (tilePane) {
    Array.from(tilePane.children).forEach((child) => {
      const zStr = (child as HTMLElement).style.zIndex;
      if (allowedZIndexes.has(zStr)) return;
      const z = Number.parseInt(zStr, 10);
      if (Number.isFinite(z) && z < 100) return;
      hideElement(child as HTMLElement);
    });
  }

  // overlayPane ではゼンリンの画像オーバーレイのみを表示。
  // URL の一致またはゼンリン関連 zIndex でオーバーレイを識別する。
  // SVG（Leaflet のデフォルトベクターレンダラー）はここで丸ごと隠さず、
  // 後段のベクター処理で path 単位に出力可否を判定する。
  const overlayPane = panes.overlayPane;
  if (overlayPane) {
    Array.from(overlayPane.children).forEach((child) => {
      if (child instanceof SVGElement) return;
      const childEl = child as HTMLElement;
      const zStr = childEl.style.zIndex;
      if (
        child instanceof HTMLImageElement &&
        zenrinImageOverlayUrl &&
        (child.currentSrc === zenrinImageOverlayUrl ||
          child.src === zenrinImageOverlayUrl)
      ) {
        return;
      }
      if (
        child instanceof HTMLImageElement &&
        zenrinZIndexStrings.includes(zStr)
      ) {
        return;
      }
      hideElement(childEl);
    });
  }

  // MapLibre の Canvas と Leaflet の SVG オーバーレイは別経路で描画されるため
  // 個別に処理する。描画図形 (drawnShapesPane 内 SVG) と
  // includeLayers 有効時の MapLibre レイヤーは保持する。
  mapInstance
    .getContainer()
    .querySelectorAll("canvas.maplibregl-canvas, svg.leaflet-zoom-animated")
    .forEach((el) => {
      if (
        exportOptions.includeDrawnShapes &&
        el instanceof SVGElement &&
        el.closest(".leaflet-drawn-shapes-pane")
      ) {
        return;
      }
      // 役所地番地図の対応エリア枠線（tilePane 配下の専用ペイン内 SVG）は
      // 地図サービス層扱い（tilePane の z<100 の子と同じ）なので、includeLayers に
      // かかわらず現在の表示状態を尊重して出力に含める
      if (
        el instanceof SVGElement &&
        el.closest(`.${COVERAGE_STROKE_PANE_CLASS}`)
      ) {
        return;
      }
      // overlayPane のデフォルトレンダラー SVG は全ベクターを 1 つの svg に
      // 描くため path 単位で制御する。地番ポリゴン（選択中の物件・クリック
      // した地番の強調表示）はピンや描画図形と同じくユーザー自身の選択の
      // 可視化なので、includeLayers にかかわらず画面の表示状態を尊重して
      // 出力に含める。それ以外（印刷範囲プレビュー枠などの UI 補助線）は
      // 出力に焼き込まない。
      if (el instanceof SVGElement && el.closest(".leaflet-overlay-pane")) {
        el.querySelectorAll("path").forEach((path) => {
          if (!path.classList.contains(MAP_EXPORT_PARCEL_POLYGON_CLASS)) {
            hideElement(path);
          }
        });
        return;
      }
      if (exportOptions.includeLayers) {
        return;
      }
      hideElement(el as HTMLElement);
    });
};
